import React, { useState, useEffect, useRef } from 'react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, AreaChart, Area,
  ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

import "../../Styling/Inventory.css";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import { Base_URL } from '../../url';
import { useAuth } from "../../Context/AuthContext";

const InventoryDashboard = () => {
  const { user } = useAuth();
  
  // State for filters
  const [filters, setFilters] = useState({
    orgId: String(user?.organisationId || ""),
    companyCode: '',
    officeCode: '',
    storeCode: '',  
    postingDate: ''
  });

  // State for filter display names
  const [filterDisplay, setFilterDisplay] = useState({
    orgName: '',
    companyName: '',
    officeName: '',
    storeName: ''
  });

  // State for dropdown options
  const [orgList, setOrgList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [officeList, setOfficeList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const dashboardRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [tableData, setTableData] = useState([]);

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    totalInventoryValue: 0,
    finishedItem: 0,
    rawValue: 0,
    packingItemValue: 0,
    officeCodeData: [],
    storeCodeData: [],
    itemTypeData: [],
    unitNameData: [],
    itemGroupData: [],
    skuUnitData: [],
    inventoryMovementData: [] // New state for inventory movement
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Professional color palette
  const COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
    '#6366f1', '#14b8a6', '#eab308', '#dc2626'
  ];

  // ✅ Convert value to lakhs
  const toLakhs = (value) => {
    return value / 100000;
  };

  // ✅ Format value for display in lakhs
  const formatLakhs = (value) => {
    return toLakhs(value).toFixed(2);
  };

  // ✅ Get stores from inventory data based on office code
  const getStoresByOffice = (officeCode) => {
    if (!officeCode || !allData.length) return [];

    const stores = [
      ...new Map(
        allData
          .filter(item => item.officeCode === officeCode)
          .map(item => [
            item.store_code,
            { 
              storeCode: item.store_code, 
              storeName: item.store_code_Description 
            }
          ])
      ).values(),
    ];

    return stores;
  };

  // ✅ Fetch all inventory data from your API
  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${Base_URL}/InventoryDashboard/GetInventoryDashboardData`);
      setAllData(res.data);
      setTableData(res.data);

      // Extract unique organizations
      const orgs = [
        ...new Map(
          res.data.map((item) => [
            item.organisation_Id,
            { 
              orgId: item.organisation_Id, 
              orgName: item.org_Description 
            },
          ])
        ).values(),
      ];
      setOrgList(orgs);

      if (user?.organisationId && orgs.length > 0) {
        const userOrg = orgs.find(org => org.orgId == user.organisationId);
        if (userOrg) {
          setFilterDisplay(prev => ({
            ...prev,
            orgName: userOrg.orgName
          }));
          
          const userCompanies = [
            ...new Map(
              res.data
                .filter((x) => x.organisation_Id == user.organisationId)
                .map((x) => [
                  x.comapny_Code,
                  { 
                    companyCode: x.comapny_Code, 
                    companyName: x.company_Code_Description 
                  },
                ])
            ).values(),
          ];
          setCompanyList(userCompanies);
        }
      }
      fetchInventoryMovementData();

    } catch (err) {
      console.error("Error fetching inventory data:", err);
      setError("Failed to fetch inventory data");
    } finally {
      setLoading(false);
    }
  };


    const fetchInventoryMovementData = async () => {
  try {
    const res = await axios.get(`${Base_URL}/InventoryDashboard/get-inventory-status`);
    
    // Extract N3 and N4 totals from response
    const totals = res.data.totalsByPostingKey || [];
    const n3Data = totals.find(t => t.postingKey === "N3");
    const n4Data = totals.find(t => t.postingKey === "N4");

    // Prepare chart data
    const movementData = [
      {
        name: "Inventory Movement",
        received: toLakhs(n3Data?.totalAmount || 0), // N3 → received
        issued: toLakhs(n4Data?.totalAmount || 0)    // N4 → issued
      }
    ];

    // Update dashboard data
    setDashboardData(prev => ({
      ...prev,
      inventoryMovementData: movementData
    }));
  } catch (error) {
    console.error("Error fetching inventory movement data:", error);
  }
};


  useEffect(() => {
    fetchInventoryData();
  }, []);

  useEffect(() => {
    if (allData.length > 0) {
      handleApplyFilters();
    }
  }, [allData, filters.orgId, filters.companyCode, filters.officeCode, filters.storeCode]);

  // Filter handlers
  const handleOrgChange = (e) => {
    const selectedOrgId = e.target.value;
    const selectedOrg = orgList.find(org => org.orgId == selectedOrgId);

    setFilters({
      orgId: selectedOrgId,
      companyCode: "",
      officeCode: "",
      storeCode: "",
      postingDate: filters.postingDate
    });

    setFilterDisplay({
      orgName: selectedOrg ? selectedOrg.orgName : "",
      companyName: "",
      officeName: "",
      storeName: ""
    });

    if (selectedOrgId) {
      const companies = [
        ...new Map(
          allData
            .filter((x) => x.organisation_Id == selectedOrgId)
            .map((x) => [
              x.comapny_Code,
              { 
                companyCode: x.comapny_Code, 
                companyName: x.company_Code_Description 
              },
            ])
        ).values(),
      ];
      setCompanyList(companies);
    } else {
      setCompanyList([]);
    }

    setOfficeList([]);
    setStoreList([]);
  };

  const handleCompanyChange = (e) => {
    const companyCode = e.target.value;
    const selectedCompany = companyList.find(company => company.companyCode == companyCode);

    setFilters({ 
      ...filters, 
      companyCode, 
      officeCode: "", 
      storeCode: "" 
    });

    setFilterDisplay({
      ...filterDisplay,
      companyName: selectedCompany ? selectedCompany.companyName : "",
      officeName: "",
      storeName: ""
    });

    if (companyCode && filters.orgId) {
      const offices = [
        ...new Map(
          allData
            .filter(
              (x) =>
                x.organisation_Id == filters.orgId &&
                x.comapny_Code == companyCode
            )
            .map((x) => [
              x.officeCode,
              { 
                officeCode: x.officeCode, 
                officeName: x.office_code_Description 
              },
            ])
        ).values(),
      ];
      setOfficeList(offices);
    } else {
      setOfficeList([]);
    }

    setStoreList([]);
  };

  const handleOfficeChange = (e) => {
    const officeCode = e.target.value;
    const selectedOffice = officeList.find(office => office.officeCode == officeCode);

    setFilters({ 
      ...filters, 
      officeCode, 
      storeCode: "" 
    });

    setFilterDisplay({
      ...filterDisplay,
      officeName: selectedOffice ? selectedOffice.officeName : "",
      storeName: ""
    });

    if (officeCode) {
      const stores = getStoresByOffice(officeCode);
      setStoreList(stores);
    } else {
      setStoreList([]);
    }
  };

  const handleStoreChange = (e) => {
    const storeCode = e.target.value;
    const selectedStore = storeList.find(store => store.storeCode == storeCode);

    setFilters({ ...filters, storeCode: storeCode });
    setFilterDisplay({
      ...filterDisplay,
      storeName: selectedStore ? selectedStore.storeName : ""
    });
  };

  // ✅ Apply Filters and Calculate Dashboard Data
  const handleApplyFilters = () => {
    setLoading(true);
    try {
      const filteredData = allData.filter((item) => {
        return (
          (!filters.orgId || item.organisation_Id == filters.orgId) &&
          (!filters.companyCode || item.comapny_Code == filters.companyCode) &&
          (!filters.officeCode || item.officeCode == filters.officeCode) &&
          (!filters.storeCode || item.store_code == filters.storeCode)
        );
      });

      setTableData(filteredData);

      // Calculate total values
      const totalInventoryValue = filteredData.reduce((sum, item) => sum + (item.total_Item_Value || 0), 0);
      
      const finishedItem = filteredData
        .filter(item => 
          item.item_type === 'FINISHED' || 
          item.item_type_Description?.toLowerCase().includes('finished') ||
          item.item_type === 'FG'
        )
        .reduce((sum, item) => sum + (item.total_Item_Value || 0), 0);
      
      const rawValue = filteredData
        .filter(item => 
          item.item_type === 'RAW' || 
          item.item_type_Description?.toLowerCase().includes('raw') ||
          item.item_type === 'RM'
        )
        .reduce((sum, item) => sum + (item.total_Item_Value || 0), 0);
      
      const packingItemValue = filteredData
        .filter(item => 
          item.item_type === 'PACKING' || 
          item.item_type_Description?.toLowerCase().includes('packing') ||
          item.item_type === 'PKG'
        )
        .reduce((sum, item) => sum + (item.total_Item_Value || 0), 0);

      // Calculate chart data using actual fields
      const officeCodeData = calculateChartData(filteredData, 'officeCode', 'office_code_Description');
      const storeCodeData = calculateChartData(filteredData, 'store_code', 'store_code_Description');
      const itemTypeData = calculateChartData(filteredData, 'item_type', 'item_type_Description');
      const unitNameData = calculateChartData(filteredData, 'unit', 'unit_Description');
      const itemGroupData = calculateChartData(filteredData, 'item_group', 'item_group_Description');
      const skuUnitData = calculateChartData(filteredData, 'item_Code', 'item_code_Description', 8);

      setDashboardData(prev => ({
        ...prev,
        totalInventoryValue,
        finishedItem,
        rawValue,
        packingItemValue,
        officeCodeData,
        storeCodeData,
        itemTypeData,
        unitNameData,
        itemGroupData,
        skuUnitData
      }));

    } catch (err) {
      console.error("Error applying filters:", err);
      setError("Failed to apply filters");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper function to calculate chart data
  const calculateChartData = (data, codeField, descriptionField, limit = 10) => {
    const groupedData = data.reduce((acc, item) => {
      const code = item[codeField];
      const description = item[descriptionField];
      const value = toLakhs(item.total_Item_Value || 0);
      
      if (code && description) {
        const key = `${code}-${description}`;
        if (!acc[key]) {
          acc[key] = { 
            name: description.length > 20 ? `${description.substring(0, 20)}...` : description,
            value: 0,
            fullName: description
          };
        }
        acc[key].value += value;
      }
      
      return acc;
    }, {});

    return Object.values(groupedData)
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  };

  // ✅ Professional Chart Components
  const renderPieChart = (data, title) => (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : data && data.length > 0 ? (
        <div className="chart-content">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => 
                  percent > 0.05 ? `${name}\n${(percent * 100).toFixed(0)}%` : ''
                }
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]} 
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `₹${Number(value).toFixed(2)} Lakh`, 
                  props.payload.fullName || name
                ]}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend 
                wrapperStyle={{
                  fontSize: '12px',
                  paddingTop: '20px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <p>No data available</p>
        </div>
      )}
    </div>
  );

  const renderBarChart = (data, title) => (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : data && data.length > 0 ? (
        <div className="chart-content">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                interval={0}
              />
              <YAxis 
                fontSize={12}
                tickFormatter={(value) => `₹${value.toFixed(1)}L`}
              />
              <Tooltip 
                formatter={(value, name, props) => [
                  `₹${Number(value).toFixed(2)} Lakh`, 
                  props.payload.fullName || 'Value'
                ]}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar 
                dataKey="value" 
                radius={[4, 4, 0, 0]}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <p>No data available</p>
        </div>
      )}
    </div>
  );

  const renderComposedChart = (data, title) => (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : data && data.length > 0 ? (
        <div className="chart-content">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
              />
              <YAxis 
                yAxisId="left"
                fontSize={12}
                tickFormatter={(value) => `₹${value.toFixed(1)}L`}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                fontSize={12}
              />
              <Tooltip 
                formatter={(value, name, props) => {
                  if (name === 'value') return [`₹${Number(value).toFixed(2)} Lakh`, 'Inventory Value'];
                  return [value, name];
                }}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar 
                yAxisId="left"
                dataKey="value" 
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="value" 
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: '#ef4444', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <p>No data available</p>
        </div>
      )}
    </div>
  );

// ✅ Replace the renderRadarChart for Unit Performance with this Pie Chart
const renderUnitDonutChart = (data, title) => (
  <div className="chart-container square-chart">
    <h3 className="chart-title">{title}</h3>
    {loading ? (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    ) : data && data.length > 0 ? (
      <div className="chart-content">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => 
                `${name}: ${(percent * 100).toFixed(0)}%`
              }
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value) => [`₹${Number(value).toFixed(2)} Lakh`, 'Value']}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <div className="no-data">
        <div className="no-data-icon">📊</div>
        <p>No unit data available</p>
      </div>
    )}
  </div>
);
  const renderBiaxialBarChart = (data, title) => (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : data && data.length > 0 ? (
        <div className="chart-content">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis 
                yAxisId="left"
                fontSize={12}
                tickFormatter={(value) => `₹${value.toFixed(0)}L`}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                fontSize={12}
                tickFormatter={(value) => `₹${value.toFixed(0)}L`}
              />
              <Tooltip 
                formatter={(value, name) => [
                  `₹${Number(value).toFixed(2)} Lakh`, 
                  name === 'received' ? 'Inventory Received' : 'Inventory Issued'
                ]}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar 
                yAxisId="left"
                dataKey="received" 
                name="Inventory Received"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                yAxisId="right"
                dataKey="issued" 
                name="Inventory Issued"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="no-data">
          <div className="no-data-icon">📊</div>
          <p>No data available</p>
        </div>
      )}
    </div>
  );

  
  const renderValueCard = (title, value, color, description) => (
    <div className={`value-card ${color}`}>
      <div className="value-content">
        <div className="value-text">
          <h3>{title}</h3>
          {loading ? (
            <div className="value-skeleton"></div>
          ) : (
            <p className="value-amount">
              ₹{formatLakhs(value)} Lakh
            </p>
          )}
          <p className="value-description">{description}</p>
        </div>
        <div className="value-icon">
          {color === 'total' && '💰'}
          {color === 'finished' && '🏭'}
          {color === 'raw' && '⚙️'}
          {color === 'packing' && '📦'}
        </div>
      </div>
    </div>
  );

  // Download functions
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const element = dashboardRef.current;
      const canvas = await html2canvas(element, { 
        scale: 2,
        useCORS: true, 
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.8);
      
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgWidthPdf = imgWidth * ratio;
      const imgHeightPdf = imgHeight * ratio;
      
      pdf.addImage(imgData, "JPEG", 0, 0, imgWidthPdf, imgHeightPdf);
      pdf.save(`InventoryDashboard_${filters.orgId || 'All'}_${new Date().toISOString().slice(0, 10)}.pdf`);
      
    } catch (err) {
      console.error("PDF Generation Error:", err);
      setError("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportExcel = () => {
    setExcelLoading(true);
    try {
      const headers = [
        'Organization',
        'Company', 
        'Office',
        'Store',
        'Item Type',
        'Unit',
        'Item Group',
        'Item Code',
        'Item Description',
        'Quantity',
        'Value (₹)'
      ];

      const csvData = tableData.map(item => [
        item.org_Description || '',
        item.company_Code_Description || '',
        item.office_code_Description || '',
        item.store_code_Description || '',
        item.item_type_Description || '',
        item.unit_Description || '',
        item.item_group_Description || '',
        item.item_Code || '',
        item.item_code_Description || '',
        item.total_Item_Quantity || 0,
        item.total_Item_Value || 0
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          row.map(field => 
            `"${String(field).replace(/"/g, '""')}"`
          ).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      const orgName = (filterDisplay.orgName || 'All').replace(/[^a-zA-Z0-9]/g, '_');
      const companyName = (filterDisplay.companyName || 'All').replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Inventory_${orgName}_${companyName}_${new Date().toISOString().slice(0, 10)}.csv`;

      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Export Error:", err);
      setError("Failed to export file. Please try again.");
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <div className="dashboard-container inventory-dashboard" ref={dashboardRef}>
      <div className="max-w-7xl mx-auto">
        {/* PDF Loading Indicator */}
        {pdfLoading && (
          <div className="pdf-loading">
            <div className="spinner"></div>
            <span>Generating PDF...</span>
          </div>
        )}
       
        <div className="inventory-title">Inventory Dashboard</div>
        
        {/* Filters Section */}
        <div className="filters-container">
          <div className="filter-grid">
            <div className="filter-group">
              <label className="filter-label">Organization ID</label>
              <select
                value={filters.orgId}
                onChange={handleOrgChange}
                className="filter-select"
              >
                <option value="">-- Select Organization --</option>
                {orgList.map((org) => (
                  <option key={org.orgId} value={org.orgId}>
                    {org.orgId}-{org.orgName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Company Code</label>
              <select
                value={filters.companyCode}
                onChange={handleCompanyChange}
                disabled={!filters.orgId}
                className="filter-select"
              >
                <option value="">-- Select Company --</option>
                {companyList.map((company) => (
                  <option key={company.companyCode} value={company.companyCode}>
                    {company.companyCode}-{company.companyName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Office Code</label>
              <select
                value={filters.officeCode}
                onChange={handleOfficeChange}
                disabled={!filters.orgId}
                className="filter-select"
              >
                <option value="">-- Select Office --</option>
                {officeList.map((office) => (
                  <option key={office.officeCode} value={office.officeCode}>
                    {office.officeCode}-{office.officeName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Store Code</label>
              <select
                value={filters.storeCode}
                onChange={handleStoreChange}
                disabled={!filters.officeCode}
                className="filter-select"
              >
                <option value="">-- Select Store --</option>
                {storeList.map((store) => (
                  <option key={store.storeCode} value={store.storeCode}>
                    {store.storeCode}-{store.storeName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Posting Date</label>
              <input
                type="date"
                value={filters.postingDate}
                onChange={(e) => setFilters({...filters, postingDate: e.target.value})}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label" style={{ opacity: 0 }}>Export</label>
              <button
                onClick={handleDownloadPDF}
                disabled={pdfLoading || loading}
                className="btn btn-export"
                style={{ 
                  width: '100%', 
                  height: '42px',
                  marginTop: '0'
                }}
              >
                {pdfLoading ? 'Generating PDF...' : 'Export to PDF'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Inventory Value Cards Grid */}
        <div className="inventory-cards-grid">
          {renderValueCard(
            'Total Inventory Value', 
            dashboardData.totalInventoryValue, 
            'total',
            'Overall inventory worth'
          )}
          
          {renderValueCard(
            'Finished Goods', 
            dashboardData.finishedItem, 
            'finished',
            'Completed products value'
          )}
          
          {renderValueCard(
            'Raw Materials', 
            dashboardData.rawValue, 
            'raw',
            'Raw material inventory'
          )}
          
          {renderValueCard(
            'Packing Materials', 
            dashboardData.packingItemValue, 
            'packing',
            'Packaging items value'
          )}
        </div>

        {/* Professional Charts Grid */}
        <div className="charts-grid">
          {renderPieChart(dashboardData.officeCodeData, 'Office Wise Distribution')}
          {renderBarChart(dashboardData.storeCodeData, 'Store Wise Inventory')}
          {renderComposedChart(dashboardData.itemTypeData, 'Item Type Analysis')}
        </div>

        <div className="charts-grid">
  {renderBiaxialBarChart(dashboardData.inventoryMovementData, 'Inventory Received vs Issued')}
  {renderUnitDonutChart(dashboardData.unitNameData, 'Unit Performance')} {/* REPLACED */}
  {renderBarChart(dashboardData.skuUnitData, 'Top SKU Units')}
</div>
        {/* Professional Table */}
        <div className="table-container">
          <div className="table-header">
            <div>
              <h3 className="table-title">Inventory Details</h3>
              <p className="table-subtitle">Complete inventory breakdown with filtering applied</p>
            </div>
            <div className="table-actions">
              <div className="table-stats">
                <span className="stat-item">Total Records: <strong>{tableData.length}</strong></span>
                <span className="stat-item">Total Value: <strong>₹{formatLakhs(dashboardData.totalInventoryValue)} Lakh</strong></span>
              </div>
              <button
                onClick={handleExportExcel}
                disabled={excelLoading || tableData.length === 0}
                className="btn btn-excel"
              >
                {excelLoading ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
          </div>
          
          <div className="table-wrapper">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Company</th>
                  <th>Office</th>
                  <th>Store</th>
                  <th>Item Type</th>
                  <th>Unit</th>
                  <th>Item Group</th>
                  <th>Item Code</th>
                  <th>Item Description</th>
                  <th>Quantity</th>
                  <th>Value (₹)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="loading-cell">
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <span>Loading inventory data...</span>
                      </div>
                    </td>
                  </tr>
                ) : tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td>{item.org_Description || '-'}</td>
                      <td>{item.company_Code_Description || '-'}</td>
                      <td>{item.office_code_Description || '-'}</td>
                      <td>{item.store_code_Description || '-'}</td>
                      <td>{item.item_type_Description || '-'}</td>
                      <td>{item.unit_Description || '-'}</td>
                      <td>{item.item_group_Description || '-'}</td>
                      <td className="code-cell">{item.item_Code || '-'}</td>
                      <td>{item.item_code_Description || '-'}</td>
                      <td className="quantity-cell">{item.total_Item_Quantity?.toLocaleString() || '0'}</td>
                      <td className="value-cell">₹{item.total_Item_Value?.toLocaleString() || '0'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="no-data-cell">
                      <div className="no-data-content">
                        <div className="no-data-icon">📭</div>
                        <div>
                          <h4>No inventory data available</h4>
                          <p>Try adjusting your filters to see more results</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDashboard;